<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import FilterInputBox from 'dashboard/components/widgets/FilterInput/Index.vue';
import useAutomationValues from 'dashboard/composables/useAutomationValues';
import {
  getCustomAttributeType,
  getInputType,
  getOperators,
} from 'dashboard/helper/automationHelper';

// One numbered condition. The number ties the row to the formula shown under
// the builder, so both refer to the same thing.
const props = defineProps({
  condition: {
    type: Object,
    required: true,
  },
  number: {
    type: Number,
    required: true,
  },
  // The last entry of a list has nothing after it to join to.
  showQueryOperator: {
    type: Boolean,
    default: false,
  },
  attributeGroups: {
    type: Array,
    default: () => [],
  },
  automationTypes: {
    type: Object,
    required: true,
  },
  automation: {
    type: Object,
    required: true,
  },
  allCustomAttributes: {
    type: Array,
    default: () => [],
  },
  mode: {
    type: String,
    default: 'create',
  },
  error: {
    type: String,
    default: '',
  },
});

defineEmits(['update:condition', 'reset', 'remove']);

const { t } = useI18n();
const { getConditionDropdownValues } = useAutomationValues();

const attributeKey = computed(() => props.condition.attribute_key);

const inputType = computed(() =>
  getInputType(
    props.allCustomAttributes,
    props.automationTypes,
    props.automation,
    attributeKey.value
  )
);

const operators = computed(() =>
  getOperators(
    props.allCustomAttributes,
    props.automationTypes,
    props.automation,
    props.mode,
    attributeKey.value
  )
);

const customAttributeType = computed(() =>
  getCustomAttributeType(
    props.automationTypes,
    props.automation,
    attributeKey.value
  )
);

const dropdownValues = computed(() =>
  getConditionDropdownValues(attributeKey.value)
);

const errorMessage = computed(() =>
  props.error ? t(`AUTOMATION.ERRORS.${props.error}`) : ''
);
</script>

<template>
  <div class="flex items-start gap-2">
    <span class="w-4 pt-4 text-xs text-right text-n-slate-10">
      {{ number }}
    </span>
    <div class="flex-grow">
      <FilterInputBox
        :model-value="condition"
        :filter-groups="attributeGroups"
        :input-type="inputType"
        :operators="operators"
        :dropdown-values="dropdownValues"
        :show-query-operator="showQueryOperator"
        :custom-attribute-type="customAttributeType"
        :error-message="errorMessage"
        @update:model-value="$emit('update:condition', $event)"
        @reset-filter="$emit('reset')"
        @remove-filter="$emit('remove')"
      />
    </div>
  </div>
</template>
